import type { SourceRestaurantData } from "../../../../../stores/appSourceRestaurantDataStore";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { InventorySlideOver, slideOverCancelClass } from "../../../../ui/InventorySlideOver";
import { greyTextColor, overlayTopLayer } from "../../../../../styling/stylingHelper";
import SwapIngredientPanel from "../../review/SwapIngredientPanel";
import { candidateUnit, ReviewWriteAdapter, SwapCandidate } from "../../review/reviewAdapter";
import {
    createCustomLinkAdapter,
    hasInventedPackSize,
    isUnitTypeChange,
    linkIdentityKey,
    toReviewIngredient,
    LinkableCustomIngredient,
} from "../../review/customLinkAdapter";
import { productKey } from "../../../../../utils/reports/shared";
import { useCostPriceStore } from "../../../../../stores/appCostPriceStore";
import { useSubrecipesStore } from "../../../../../stores/appSubrecipesStore";

interface LinkCustomIngredientModalProps {
    /** The custom row the user clicked Link on. */
    ingredient: LinkableCustomIngredient & { id: string };
    /** Cost price id or subrecipe id — whichever record the row lives in. */
    itemId: string;
    dataOverride?: SourceRestaurantData;
    /**
     * The order-history rows this dish/prep ALREADY carries. The picker has no exclusion
     * list on purpose — a recipe may legitimately use one product in two prep steps — so
     * picking one of these is allowed, but never silent: it is annotated in the candidate
     * list with the amount already in use.
     */
    existingRows: Array<{ key: string; quantityNeeded?: number; stdUnit?: string }>;
    defaultWastePercentage: number;
    /** Writes the source row — immediate for a dish, draft-local for a subrecipe. */
    onApply: (candidate: SwapCandidate, quantity: number) => Promise<void>;
    /**
     * Whether to offer "apply to the other places that use this too" after the swap
     * (default true). Pass false when `onApply` does NOT persist — see the adapter config.
     */
    offerApplyElsewhere?: boolean;
    onClose: () => void;
}

/**
 * Slide-over layered above the cost price / subrecipe detail panel that re-points a
 * CUSTOM ingredient row (a placeholder price the user typed) at a real order-history
 * product or a subrecipe.
 *
 * The picker itself is `SwapIngredientPanel`, used verbatim: the adapter's
 * `allowedSwapKinds` drops the Custom Ingredients tab (and custom ingredients from
 * the Suggested list), and `toReviewIngredient` mirrors the live row into the
 * `template*` fields the panel searches and pre-fills quantities from.
 */
const LinkCustomIngredientModal: React.FC<LinkCustomIngredientModalProps> = ({
    ingredient,
    itemId,
    dataOverride,
    existingRows,
    defaultWastePercentage,
    onApply,
    offerApplyElsewhere = true,
    onClose,
}) => {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);

    // Slide-in on mount / slide-out before unmount, the same idiom as the other
    // cost price side panels.
    useEffect(() => { setIsOpen(true); }, []);
    const handleClose = () => {
        setIsOpen(false);
        setTimeout(onClose, 300);
    };

    // The write callback can change identity between renders; the adapter must not.
    const applyRef = useRef(onApply);
    useEffect(() => { applyRef.current = onApply; }, [onApply]);

    // Created ONCE: the adapter remembers which domain each fan-out target belongs
    // to between `findSameIngredientElsewhere` and the `swap` calls
    // `ApplyToOtherItemsPanel` makes, and a rebuilt adapter would lose that map
    // (every fan-out apply would then rewrite the source row instead).
    const adapterRef = useRef<ReviewWriteAdapter | null>(null);
    if (!adapterRef.current) {
        adapterRef.current = createCustomLinkAdapter({
            source: { itemId, rowId: ingredient.id, identityKey: linkIdentityKey(ingredient), row: ingredient },
            applyToSource: (candidate, quantity) => applyRef.current(candidate, quantity),
            getAnalyses: () => useCostPriceStore.getState().costPriceList,
            getSubrecipes: () => useSubrecipesStore.getState().subrecipesList,
            prepLabel: (name) => t('costPrice.customLink.applyElsewhereUsedInPrep', { name }),
            offerApplyElsewhere,
        });
    }

    // Keyed by canonical product identity, never by name (dataRules rule 8).
    const existingByKey = useMemo(
        () => new Map(existingRows.map(row => [row.key, row])),
        [existingRows]
    );
    const sourceUnit = ingredient.quantityInfo?.stdUnit;

    /**
     * The two annotations spec 1 §2 requires. Both are things only this flow knows, which
     * is why they arrive through `candidateNote` rather than living in the shared panel.
     */
    /** A unit is sometimes genuinely absent — say so rather than leaving a hole in the sentence. */
    const unitLabel = (unit: string | undefined) => (unit && unit.trim() !== '' ? unit : t('costPrice.customLink.noUnit'));

    const renderCandidateNote = (candidate: SwapCandidate, placement: 'list' | 'selected') => {
        // The picker invented this candidate's pack size, so the whole line price is being
        // counted as one piece. Warn, never block — the product may still be the right one.
        const packSizeNote = hasInventedPackSize(candidate) ? (
            <span className="block text-[11px] mt-0.5 text-amber-600 dark:text-amber-400">
                {t('costPrice.customLink.noPackSize')}
            </span>
        ) : null;

        if (placement === 'list') {
            const existing = candidate.kind === 'product'
                ? existingByKey.get(productKey(candidate.platform ?? '', candidate.articleId, candidate.displayName))
                : undefined;
            if (!existing && !packSizeNote) return null;
            return (
                <>
                    {existing && (
                        <span className={`block text-[11px] mt-0.5 ${greyTextColor}`}>
                            {t('costPrice.customLink.alreadyOnDish', {
                                qty: existing.quantityNeeded ?? '?',
                                unit: existing.stdUnit ?? '',
                            })}
                        </span>
                    )}
                    {packSizeNote}
                </>
            );
        }

        // Cross-type swaps are where `defaultQuantityFor` falls back to its 100/1 DEFAULT.
        // The panel's own "recipe amount" line already goes italic-not-convertible here;
        // this states the reason rather than leaving it to be inferred.
        const targetUnit = candidateUnit(candidate);
        const unitNote = isUnitTypeChange(sourceUnit, targetUnit) ? (
            <p className="text-[12px] text-amber-600 dark:text-amber-400">
                {t('costPrice.customLink.unitChanged', { from: unitLabel(sourceUnit), to: unitLabel(targetUnit) })}
            </p>
        ) : null;
        if (!unitNote && !packSizeNote) return null;
        return <>{unitNote}{packSizeNote}</>;
    };

    return (
        <InventorySlideOver
            isOpen={isOpen}
            onClose={handleClose}
            title={t('costPrice.customLink.title')}
            subtitle={ingredient.productName}
            description={t('costPrice.customLink.description')}
            closeLabel={t('common.close')}
            zIndexClass={overlayTopLayer}
            footer={(
                <button type="button" onClick={handleClose} className={slideOverCancelClass}>
                    {t('common.cancel')}
                </button>
            )}
        >
            <p className={`text-[13px] leading-5 ${greyTextColor}`}>
                {t('costPrice.customLink.tabsHint')}
            </p>

            <SwapIngredientPanel
                ingredient={toReviewIngredient(ingredient)}
                dataOverride={dataOverride}
                // -1 marks the source row: fan-out targets are keyed by their own
                // index in their item, which is never negative.
                ingredientIndex={-1}
                itemId={itemId}
                defaultWastePercentage={defaultWastePercentage}
                adapter={adapterRef.current}
                candidateNote={renderCandidateNote}
                onDone={handleClose}
            />
        </InventorySlideOver>
    );
};

export default LinkCustomIngredientModal;
